import { DestroyRef, Injectable, Signal, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Client, QtyUnreadMessages } from '@api/client';
import { AppStateChangeService } from '@services/app-state-change.service';
import { AuthService } from '@services/auth.service';
import { PlatformService } from '@services/platform.service';
import { WebSocketController } from '@services/web-socket.controller';
import { NotificationUnreadCountEvent, SOCKET_EVENT_NAMES } from '@services/web-socket.interfaces';
import {
  BehaviorSubject, EMPTY, Observable, Subject, ignoreElements, catchError, debounceTime, distinctUntilChanged,
  exhaustMap, filter, fromEvent, map, merge, of, retry, share, shareReplay, skip, startWith,
  switchMap, takeUntil, tap, throwError, timer, withLatestFrom,
} from 'rxjs';

@Injectable({providedIn: 'root'})
export class UnreadCountersService {
  private readonly client = inject(Client);
  private readonly auth = inject(AuthService);
  private readonly socket = inject(WebSocketController);
  private readonly platform = inject(PlatformService);
  private readonly appState = inject(AppStateChangeService);
  private readonly destroyRef = inject(DestroyRef);

  public readonly notificationCount: Signal<number>;
  public readonly chatCounts$: Observable<QtyUnreadMessages>;

  private readonly refreshRequested = new Subject<void>();
  private readonly requestsCancelled = new Subject<void>();
  public readonly refreshRequested$ = this.refreshRequested.asObservable();
  public readonly requestsCancelled$ = this.requestsCancelled.asObservable();
  private foreground = false;

  private readonly notifications = signal(0);
  private readonly chats = new BehaviorSubject<QtyUnreadMessages>(new QtyUnreadMessages());

  public get isForeground(): boolean {
    return this.foreground;
  }

  constructor() {
    this.notificationCount = this.notifications.asReadonly();
    this.chatCounts$ = this.chats.asObservable();

    if (!this.platform.isBrowser) return;

    this.auth.parsedToken$.pipe(
      map(token => token?.userId),
      distinctUntilChanged(),
      tap((): void => {
        this.foreground = false;
        this.requestsCancelled.next();
        this.notifications.set(0);
        this.chats.next(new QtyUnreadMessages());
      }),
      switchMap(userId => userId ? this.watchCounters(userId) : EMPTY),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe();
  }

  private watchCounters(userId: number): Observable<unknown> {
    const active$: Observable<boolean> = (this.platform.isDevice
      ? this.appState.appStateChange$
      : fromEvent(document, 'visibilitychange').pipe(
        map((): boolean => document.visibilityState === 'visible'),
        startWith(document.visibilityState === 'visible'),
      )).pipe(tap(active => this.foreground = active), distinctUntilChanged(), shareReplay({bufferSize: 1, refCount: true}));

    const ready$ = this.socket.isSocketInitedSubject.pipe(distinctUntilChanged());

    const cancelled$ = merge(
      active$.pipe(skip(1), filter(active => !active)),
      ready$.pipe(skip(1), filter(ready => !ready)),
    );

    const refresh$ = merge(
      of(undefined),
      ready$.pipe(filter(Boolean)),
      active$.pipe(skip(1), filter(Boolean)),
      this.platform.isDevice ? this.appState.appResume$ : EMPTY,
    ).pipe(
      // Share the reasons before debouncing so list and counter refreshes use one coalescing window.
      tap(() => this.refreshRequested.next()),
      debounceTime(250),
      withLatestFrom(active$),
      filter(([, active]): boolean => active),
      share(),
    );

    const notificationUpdates$ = this.socket.notificationEvent.pipe(
      filter(event => event.type === SOCKET_EVENT_NAMES.NOTIFICATION_UNREAD_COUNT),
      map((event): NotificationUnreadCountEvent | undefined => event.data),
      filter(counter => counter?.user_id === userId && Number.isInteger(counter.count) && counter.count >= 0),
      map(counter => counter.count),
      share(),
    );

    const chatUpdates$ = this.socket.chatEvent.pipe(
      filter(event => event.type === SOCKET_EVENT_NAMES.QTY_UNREAD_MESSAGES && this.socket.isSocketInitedSubject.value),
      map((event): QtyUnreadMessages => event.data),
      share(),
    );

    return merge(
      cancelled$.pipe(tap(() => this.requestsCancelled.next()), ignoreElements()),
      this.counterValues(refresh$, notificationUpdates$, cancelled$, () => this.client.notificationUnreadCount()).pipe(
        tap(count => this.notifications.set(count)),
      ),
      this.counterValues(refresh$, chatUpdates$, cancelled$, () => this.client.chatQtyUnreadMessages()).pipe(
        tap(count => this.chats.next(count)),
      ),
    );
  }

  private counterValues<T>(
    refresh$: Observable<unknown>,
    updates$: Observable<T>,
    cancelled$: Observable<unknown>,
    load: () => Observable<T>,
  ): Observable<T> {
    return merge(
      updates$,
      refresh$.pipe(
        exhaustMap(() => load().pipe(
          // At most three attempts per refresh; successful reads never schedule another request.
          retry({
            count: 2,
            delay: (error: {status?: number} | null, retryCount: number) => {
              const status = error?.status;
              return status === 0 || status === 408 || (status >= 500 && status < 600)
                ? timer(retryCount === 1 ? 1000 : 3000)
                : throwError(() => error);
            },
          }),
          // A socket value supersedes HTTP; logout, disconnect and background also cancel pending reads.
          takeUntil(merge(updates$, cancelled$)),
          catchError((error): Observable<never> => {
            console.error('Error loading unread count', error);
            return EMPTY;
          }),
        )),
      ),
    );
  }
}
